import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Select, message } from "antd";
import AdminJewelryReportService from "../../../services/admin-jewelryReport-service";
import ReportScaffold from "../../ChartsGroup/DiamondReportsCharts/_shared/ReportScaffold";
import { exportChartPdf } from "../../ChartsGroup/DiamondReportsCharts/_shared/exportChartPdf";
import { BlobToDownloadFile } from "../../../helper/commanFunction";
import { GLOBAL_METAL_TYPE_CONFIG } from "./_shared/globalJewelryReportConfigs";
import { isValidMetalType } from "../jewelry/_shared/isValidMetalType";
import JewelryDetailSearchTable from "../jewelry/_shared/JewelryDetailSearchTable";
import { extractDealerIds, fetchPriorPeriodTotalClicks } from "../jewelry/_shared/jewelryHelpers";
import { GLOBAL_REPORT_PERCENT_ONLY, GLOBAL_REPORT_PRIOR_LAST_YEAR } from "../_shared/globalReportSettings";

const { Option } = Select;

const GlobalJewelryMetalTypeReport = () => {
  const { t } = useTranslation();
  const loginDetials = useSelector((state) => state.loginReducer);

  const [dealerId, setDealerId] = useState();
  const [forDealer, setForDealer] = useState("");
  const [dealerMemberTypeName, setDealerMemberTypeName] = useState("");
  const [dataRangeFrom, setDataRangeFrom] = useState(moment().subtract(30, "days").format("YYYY-MM-DD"));
  const [dataRangeTo, setDataRangeTo] = useState(moment().format("YYYY-MM-DD"));
  const [vendorIds, setVendorIds] = useState([]);
  const [retailerIds, setRetailerIds] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [retailerList, setRetailerList] = useState([]);
  const [metalTypeData, setMetalTypeData] = useState([]);
  const [totalClicks, setTotalClicks] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refetchToken, setRefetchToken] = useState(0);
  const [filterMetalTypeId, setFilterMetalTypeId] = useState("");
  const [filterMetalColorId, setFilterMetalColorId] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [activityPdfLoading, setActivityPdfLoading] = useState(false);
  const [chartType, setChartType] = useState("doughnut");
  const [priorTotalClicks, setPriorTotalClicks] = useState(null);
  // All jewelry clicks (Category total) for the same dates - the base of the
  // percent-only Total Clicks card (Task 2344).
  const [allJewelryClicks, setAllJewelryClicks] = useState(null);

  useEffect(() => {
    if (loginDetials?.loginDetials?.responseData?.logInSucceeded) {
      const id = loginDetials.loginDetials.responseData.dealerId;
      setDealerId(id);
      setDealerMemberTypeName(loginDetials.loginDetials.responseData.dealerMemberTypeName);
      handleGetForDealer(id);
    }
    handleGetVendorDropdownData();
    handleGetRetailerDropdownData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGetForDealer = (id) => {
    AdminJewelryReportService.GetForDealer({ dealerID: id }).then((response) => {
      if (response.data.message === "Success") {
        const fd = response.data.responseData.forDealer;
        setForDealer(fd);
        handleLoadMetalTypeData(id, fd, dataRangeFrom, dataRangeTo, [], []);
      }
    });
  };

  const handleGetVendorDropdownData = () => {
    const id = loginDetials?.loginDetials?.responseData?.dealerId;
    AdminJewelryReportService.GetVendorDropdownList({ dealerId: id, reporttype: "global" }).then((response) => {
      if (response.data.message === "Success") setVendorList(response.data.responseData);
    });
  };

  const handleGetRetailerDropdownData = () => {
    AdminJewelryReportService.GetRetailerDropdownList({ dealerId: 0, reporttype: "global" }).then((response) => {
      if (response.data.message === "Success") setRetailerList(response.data.responseData);
    });
  };

  const handleVendorChange = (values) => {
    setVendorIds(values);
    if (values.length === 0) {
      handleGetRetailerDropdownData();
    } else {
      AdminJewelryReportService.ChangeRetailerList({ vendorIds: extractDealerIds(values).toString() }).then((response) => {
        if (response.data.message === "Success") setRetailerList(response.data.responseData);
      });
    }
  };

  const handleRetailerChange = (values) => {
    setRetailerIds(values);
    if (values.length === 0) {
      handleGetVendorDropdownData();
    } else {
      AdminJewelryReportService.ChangeVendorList({ retailerIds: extractDealerIds(values).toString() }).then((response) => {
        if (response.data.message === "Success") setVendorList(response.data.responseData);
      });
    }
  };

  const handleLoadMetalTypeData = (dealerIdArg, forDealerArg, fromDate, toDate, vIds, rIds) => {
    setLoading(true);
    AdminJewelryReportService.GetMetalTypeData({
      dealerID: dealerIdArg,
      forDealer: forDealerArg,
      fromDate: moment(fromDate).format("YYYY-MM-DD"),
      toDate: moment(toDate).format("YYYY-MM-DD"),
      retailerIds: extractDealerIds(rIds).toString(),
      vendorIds: extractDealerIds(vIds).toString(),
    })
      .then((response) => {
        setLoading(false);
        if (response.data.message === "Success" && response.data.responseData) {
          const rows = response.data.responseData;
          setMetalTypeData(rows);
          setTotalClicks(rows.reduce((sum, row) => sum + Number(row.count || 0), 0));
        } else {
          setMetalTypeData([]);
          setTotalClicks(0);
        }
      })
      .catch(() => {
        setLoading(false);
        setMetalTypeData([]);
        setTotalClicks(0);
      });

    const allParams = {
      dealerID: dealerIdArg,
      forDealer: forDealerArg,
      fromDate: moment(fromDate).format("YYYY-MM-DD"),
      toDate: moment(toDate).format("YYYY-MM-DD"),
      retailerIds: extractDealerIds(rIds).toString(),
      vendorIds: extractDealerIds(vIds).toString(),
    };
    setAllJewelryClicks(null);
    setPriorTotalClicks(null);
    if (GLOBAL_REPORT_PERCENT_ONLY) {
      // Percent-only: Total Clicks = this page's clicks / all jewelry clicks.
      AdminJewelryReportService.GetClicksBasedOnCategory(allParams)
        .then((response) => {
          const rows = response.data.message === "Success" ? response.data.responseData || [] : [];
          setAllJewelryClicks(rows.reduce((sum, row) => sum + Number(row.count || 0), 0));
        })
        .catch(() => setAllJewelryClicks(0));
      return;
    }
    fetchPriorPeriodTotalClicks(
      AdminJewelryReportService.GetMetalTypeData,
      (pFrom, pTo) => ({
        dealerID: dealerIdArg,
        forDealer: forDealerArg,
        fromDate: pFrom,
        toDate: pTo,
        retailerIds: extractDealerIds(rIds).toString(),
        vendorIds: extractDealerIds(vIds).toString(),
      }),
      fromDate,
      toDate,
      "count",
      (data) => data,
      GLOBAL_REPORT_PRIOR_LAST_YEAR
    ).then(setPriorTotalClicks);
  };

  const onDateChange = (dates, dateStrings) => {
    if (dates) {
      setDataRangeFrom(dateStrings[0]);
      setDataRangeTo(dateStrings[1]);
    }
  };

  const handleApplyFilter = () => {
    setFilterMetalTypeId("");
    setFilterMetalColorId("");
    handleLoadMetalTypeData(dealerId, forDealer, dataRangeFrom, dataRangeTo, vendorIds, retailerIds);
    setRefetchToken((tok) => tok + 1);
  };

  const handleSliceClick = ({ metalTypeId, metalColorId }) => {
    // MetalTypeID/MetalColorID come off the chart's raw data row as numbers;
    // the backend's MetalType/MetalColorType fields are typed string, so a
    // bare number here fails model binding (400) and the table never updates.
    setFilterMetalTypeId(metalTypeId != null ? String(metalTypeId) : "");
    setFilterMetalColorId(metalColorId != null ? String(metalColorId) : "");
  };

  const handleDownloadChart = async () => {
    setPdfLoading(true);
    const vendorNames = vendorIds.length === 0 ? t("All Vendors") : vendorList.filter((v) => extractDealerIds(vendorIds).includes(v.dealerID.toString())).map((v) => v.dealerCompany).join(", ");
    const retailerNames = retailerIds.length === 0 ? t("All Retailers") : retailerList.filter((v) => extractDealerIds(retailerIds).includes(v.dealerID.toString())).map((v) => v.dealerCompany).join(", ");
    try {
      await exportChartPdf({
        elementId: "pdf-content",
        title: t(GLOBAL_METAL_TYPE_CONFIG.pageTitle),
        dealerCompany: loginDetials?.loginDetials?.responseData?.dealerCompany,
        dealerId,
        dataRangeFrom,
        dataRangeTo,
        infoLines: [
          ...(dealerMemberTypeName !== "Vendor" ? [`Vendors: ${vendorNames}`] : []),
          ...(dealerMemberTypeName === "Admin" ? [`Retailers: ${retailerNames}`] : []),
          // Percent-only: no click count line (the share of the total would always be 100%).
          ...(GLOBAL_REPORT_PERCENT_ONLY ? [] : [`Total Clicks: ${totalClicks.toLocaleString()}`]),
        ],
        fileName: "Global-Jewelry-Metal-Type-Report.pdf",
      });
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDownloadActivityPdf = () => {
    let inputData = {
      dealerID: dealerId,
      forDealer: forDealer,
      fromDate: moment(dataRangeFrom).format("YYYY-MM-DD"),
      toDate: moment(dataRangeTo).format("YYYY-MM-DD"),
      retailerIds: extractDealerIds(retailerIds).toString(),
      vendorIds: extractDealerIds(vendorIds).toString(),
      reportName: "MetalType",
      barViewDimensions: chartType === "bar" ? "MetalType" : "",
      percentOnly: GLOBAL_REPORT_PERCENT_ONLY,
      priorLastYear: GLOBAL_REPORT_PRIOR_LAST_YEAR,
    };
    setActivityPdfLoading(true);
    AdminJewelryReportService.ExportMetricPdf(inputData)
      .then((response) => {
        setActivityPdfLoading(false);
        const filename = `GlobalJewelry_MetalType_${dataRangeFrom}_${dataRangeTo}.pdf`;
        BlobToDownloadFile(response.data, filename, "pdf");
      })
      .catch(() => {
        setActivityPdfLoading(false);
        message.error(t("Failed to generate PDF. Please try again."));
      });
  };

  const validMetalTypes = metalTypeData.filter((row) => isValidMetalType(row.metalType));
  const sortedMetalTypes = [...validMetalTypes].sort((a, b) => Number(b.count || 0) - Number(a.count || 0));
  const topMetalType = sortedMetalTypes[0];
  const validTotal = validMetalTypes.reduce((sum, row) => sum + Number(row.count || 0), 0);
  const top3Sum = sortedMetalTypes.slice(0, 3).reduce((sum, row) => sum + Number(row.count || 0), 0);
  // Percent-only: 2 decimals, same as the Top-3 Concentration card (Task 2344).
  const top3Pct = validTotal > 0 ? (GLOBAL_REPORT_PERCENT_ONLY ? Number(((top3Sum / validTotal) * 100).toFixed(2)) : Math.round((top3Sum / validTotal) * 100)) : 0;
  const topPct = validTotal > 0 && topMetalType ? ((Number(topMetalType.count) / validTotal) * 100).toFixed(2) : 0;

  return (
    <ReportScaffold
      config={GLOBAL_METAL_TYPE_CONFIG}
      report={{
        [GLOBAL_METAL_TYPE_CONFIG.dataKey]: metalTypeData,
        JewelryTotalClicks: [{ TotalClickCnt: totalClicks }],
        JewelryAllClicks: allJewelryClicks != null ? [{ TotalClickCnt: allJewelryClicks }] : [],
        JewelrySummaryKPIs: priorTotalClicks != null ? [{ Metric: "TotalClicks", CurrentValue: totalClicks, PriorValue: priorTotalClicks }] : [],
      }}
      loading={loading}
      dealerId={dealerId}
      forDealer={forDealer}
      dealerCompany={loginDetials?.loginDetials?.responseData?.dealerCompany}
      globalDiamonds={{ dataRangeFrom, dataRangeTo }}
      onDateChange={onDateChange}
      onApplyFilter={handleApplyFilter}
      refetchToken={refetchToken}
      onDownloadChart={handleDownloadChart}
      downloadingChart={pdfLoading}
      onDownloadActivityPdf={handleDownloadActivityPdf}
      downloadingActivityPdf={activityPdfLoading}
      onChartClick={handleSliceClick}
      onChartTypeChange={setChartType}
      insight={
        topMetalType && (
          <div className="edr-insight">
            💡 <b>{topMetalType.metalType}</b> {t("leads metal preference at")} <b>{topPct}%</b>, {t("and the top 3 metal types capture")}{" "}
            <b>{top3Pct}%</b> {t("of engagement")} —{" "}
            {top3Pct > 70
              ? t("a highly concentrated preference worth reflecting in inventory mix.")
              : t("preference is reasonably spread across metal types.")}
          </div>
        )
      }
      extraFilterFields={null}
      /* Bug 1840: Vendor(s)/Retailer(s) dropdowns removed from Global reports
          (expose vendor/retailer private data at the Global scope) - kept here
          commented out in case this filter is needed again in the future.
          NOTE: extraFilterFields={null} above is required (not just omitting the
          prop) - ReportScaffold falls back to a Diamond-only "Select Diamond"
          filter when this prop is undefined, which doesn't belong on Jewelry reports.
      extraFilterFields={
        <>
          {dealerMemberTypeName === "Admin" && (
            <>
              <div className="col-md-3">
                <div className="input__block">
                  <label>{t("Vendor(s)")}</label>
                  <Select className="border__grey" showSearch showArrow mode="multiple" placeholder={t("Select One or More Vendors")} optionFilterProp="children" value={vendorIds} onChange={handleVendorChange}>
                    {vendorList.map((item) => (
                      <Option key={`vendor-${item.dealerID}`} value={`${item.dealerID}|${item.dealerCompany}`}>{item.dealerCompany}</Option>
                    ))}
                  </Select>
                </div>
              </div>
              <div className="col-md-3">
                <div className="input__block">
                  <label>{t("Retailer(s)")}</label>
                  <Select className="border__grey" showSearch showArrow mode="multiple" placeholder={t("Select one or more retailer")} optionFilterProp="children" value={retailerIds} onChange={handleRetailerChange}>
                    {retailerList.map((item) => (
                      <Option key={`retailer-${item.dealerID}`} value={`${item.dealerID}|${item.dealerCompany}`}>{item.dealerCompany}</Option>
                    ))}
                  </Select>
                </div>
              </div>
            </>
          )}
          {dealerMemberTypeName === "Vendor" && (
            <div className="col-md-3">
              <div className="input__block">
                <label>{t("Retailer(s)")}</label>
                <Select className="border__grey" showSearch showArrow mode="multiple" placeholder={t("Select one or more retailer")} optionFilterProp="children" value={retailerIds} onChange={handleRetailerChange}>
                  {retailerList.map((item) => (
                    <Option key={`retailer-${item.dealerID}`} value={`${item.dealerID}|${item.dealerCompany}`}>{item.dealerCompany}</Option>
                  ))}
                </Select>
              </div>
            </div>
          )}
          {dealerMemberTypeName !== "Admin" && dealerMemberTypeName !== "Vendor" && (
            <div className="col-md-3">
              <div className="input__block">
                <label>{t("Vendor(s)")}</label>
                <Select className="border__grey" showSearch showArrow mode="multiple" placeholder={t("Select one or more vendor")} optionFilterProp="children" value={vendorIds} onChange={handleVendorChange}>
                  {vendorList.map((item) => (
                    <Option key={`vendor-${item.dealerID}`} value={`${item.dealerID}|${item.dealerCompany}`}>{item.dealerCompany}</Option>
                  ))}
                </Select>
              </div>
            </div>
          )}
        </>
      }
      */
      detailTable={
        <JewelryDetailSearchTable
          dealerId={dealerId}
          forDealer={forDealer}
          dataRangeFrom={dataRangeFrom}
          dataRangeTo={dataRangeTo}
          vendorIds={vendorIds}
          retailerIds={retailerIds}
          dealerMembershipType={dealerMemberTypeName}
          filterParamName={GLOBAL_METAL_TYPE_CONFIG.detail.filterParamName}
          metalTypeId={filterMetalTypeId}
          metalColorId={filterMetalColorId}
          fileNamePart={GLOBAL_METAL_TYPE_CONFIG.detail.fileNamePart}
          dealerCompany={loginDetials?.loginDetials?.responseData?.dealerCompany}
          refetchToken={refetchToken}
          reportType="Global"
          hideRowCount={GLOBAL_REPORT_PERCENT_ONLY}
          title={t("Product Click Log")}
        />
      }
    />
  );
};

export default GlobalJewelryMetalTypeReport;
